import React, { useState, useEffect } from 'react';
import { FaCog, FaBell, FaUser, FaGlobe, FaSave } from 'react-icons/fa';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import LoadingSpinner from '../common/LoadingSpinner';

/**
 * Component for parents to configure account settings
 */
const ParentSettings = () => {
  const { showToast } = useToast();
  
  // State for settings data
  const [settings, setSettings] = useState({
    notifications: {
      emailNotifications: true,
      pushNotifications: true,
      childActivityAlerts: true,
      mentorSessionReminders: true,
      schoolAnnouncements: true,
      marketingEmails: false
    },
    privacy: {
      shareChildProgress: false,
      allowMentorContact: true,
      showProfileToSchools: true,
      allowRecommendations: true
    },
    preferences: {
      language: 'en',
      timezone: 'Africa/Nairobi',
      dateFormat: 'MM/DD/YYYY',
      currency: 'KES',
      theme: 'light'
    },
    childAccess: {
      allowChildLogin: false,
      restrictedAccess: true,
      contentRestrictions: 'moderate',
      screenTimeLimit: 120
    }
  });
  
  // Component state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Fetch settings data on component mount
  useEffect(() => {
    fetchSettings();
  }, []);
  
  // Fetch settings from API
  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/parents/settings');
      
      // Set settings with response data or use defaults for missing data
      setSettings({
        notifications: {
          emailNotifications: response.data.data.notifications?.emailNotifications ?? true,
          pushNotifications: response.data.data.notifications?.pushNotifications ?? true,
          childActivityAlerts: response.data.data.notifications?.childActivityAlerts ?? true,
          mentorSessionReminders: response.data.data.notifications?.mentorSessionReminders ?? true,
          schoolAnnouncements: response.data.data.notifications?.schoolAnnouncements ?? true,
          marketingEmails: response.data.data.notifications?.marketingEmails ?? false
        },
        privacy: {
          shareChildProgress: response.data.data.privacy?.shareChildProgress ?? false,
          allowMentorContact: response.data.data.privacy?.allowMentorContact ?? true,
          showProfileToSchools: response.data.data.privacy?.showProfileToSchools ?? true,
          allowRecommendations: response.data.data.privacy?.allowRecommendations ?? true
        },
        preferences: {
          language: response.data.data.preferences?.language ?? 'en',
          timezone: response.data.data.preferences?.timezone ?? 'Africa/Nairobi',
          dateFormat: response.data.data.preferences?.dateFormat ?? 'MM/DD/YYYY',
          currency: response.data.data.preferences?.currency ?? 'KES',
          theme: response.data.data.preferences?.theme ?? 'light'
        },
        childAccess: {
          allowChildLogin: response.data.data.childAccess?.allowChildLogin ?? false,
          restrictedAccess: response.data.data.childAccess?.restrictedAccess ?? true,
          contentRestrictions: response.data.data.childAccess?.contentRestrictions ?? 'moderate',
          screenTimeLimit: response.data.data.childAccess?.screenTimeLimit ?? 120
        }
      });
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching settings:', error);
      showToast('Failed to load settings', 'error');
      setLoading(false);
    }
  };
  
  // Handle notification settings change
  const handleNotificationChange = (e) => {
    const { name, checked } = e.target;
    
    setSettings({
      ...settings,
      notifications: {
        ...settings.notifications,
        [name]: checked
      }
    });
  };
  
  // Handle privacy settings change
  const handlePrivacyChange = (e) => {
    const { name, checked } = e.target;
    
    setSettings({
      ...settings,
      privacy: {
        ...settings.privacy,
        [name]: checked
      }
    });
  };
  
  // Handle preference settings change
  const handlePreferenceChange = (e) => {
    const { name, value } = e.target;
    
    setSettings({
      ...settings,
      preferences: {
        ...settings.preferences,
        [name]: value
      }
    });
  };
  
  // Handle child access settings change
  const handleChildAccessChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setSettings({
      ...settings,
      childAccess: {
        ...settings.childAccess,
        [name]: type === 'checkbox' ? checked : value
      }
    });
  };
  
  // Handle save settings
  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      
      await api.put('/parents/settings', settings);
      
      showToast('Settings saved successfully', 'success');
    } catch (error) {
      console.error('Error saving settings:', error);
      showToast('Failed to save settings', 'error');
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <div className="container mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold mb-6 flex items-center">
        <FaCog className="mr-2 text-primary" /> Account Settings
      </h1>
      
      {loading ? (
        <div className="bg-white rounded-lg shadow-soft p-6 flex justify-center">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Notification Settings */}
          <div className="bg-white rounded-lg shadow-soft p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <FaBell className="mr-2 text-primary" /> Notification Settings
            </h2>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label htmlFor="emailNotifications" className="text-gray-700">
                  Email Notifications
                </label>
                <div className="relative inline-block w-10 mr-2 align-middle select-none">
                  <input
                    type="checkbox"
                    id="emailNotifications"
                    name="emailNotifications"
                    checked={settings.notifications.emailNotifications}
                    onChange={handleNotificationChange}
                    className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                  />
                  <label
                    htmlFor="emailNotifications"
                    className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"
                  ></label>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <label htmlFor="pushNotifications" className="text-gray-700">
                  Push Notifications
                </label>
                <div className="relative inline-block w-10 mr-2 align-middle select-none">
                  <input
                    type="checkbox"
                    id="pushNotifications"
                    name="pushNotifications"
                    checked={settings.notifications.pushNotifications}
                    onChange={handleNotificationChange}
                    className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                  />
                  <label
                    htmlFor="pushNotifications"
                    className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"
                  ></label>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <label htmlFor="childActivityAlerts" className="text-gray-700">
                  Child Activity Alerts
                </label>
                <div className="relative inline-block w-10 mr-2 align-middle select-none">
                  <input
                    type="checkbox"
                    id="childActivityAlerts"
                    name="childActivityAlerts"
                    checked={settings.notifications.childActivityAlerts}
                    onChange={handleNotificationChange}
                    className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                  />
                  <label
                    htmlFor="childActivityAlerts"
                    className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"
                  ></label>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <label htmlFor="mentorSessionReminders" className="text-gray-700">
                  Mentor Session Reminders
                </label>
                <div className="relative inline-block w-10 mr-2 align-middle select-none">
                  <input
                    type="checkbox"
                    id="mentorSessionReminders"
                    name="mentorSessionReminders"
                    checked={settings.notifications.mentorSessionReminders}
                    onChange={handleNotificationChange}
                    className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                  />
                  <label
                    htmlFor="mentorSessionReminders"
                    className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"
                  ></label>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <label htmlFor="schoolAnnouncements" className="text-gray-700">
                  School Announcements
                </label>
                <div className="relative inline-block w-10 mr-2 align-middle select-none">
                  <input
                    type="checkbox"
                    id="schoolAnnouncements"
                    name="schoolAnnouncements"
                    checked={settings.notifications.schoolAnnouncements}
                    onChange={handleNotificationChange}
                    className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                  />
                  <label
                    htmlFor="schoolAnnouncements"
                    className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"
                  ></label>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <label htmlFor="marketingEmails" className="text-gray-700">
                  Marketing Emails
                </label>
                <div className="relative inline-block w-10 mr-2 align-middle select-none">
                  <input
                    type="checkbox"
                    id="marketingEmails"
                    name="marketingEmails"
                    checked={settings.notifications.marketingEmails}
                    onChange={handleNotificationChange}
                    className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                  />
                  <label
                    htmlFor="marketingEmails"
                    className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"
                  ></label>
                </div>
              </div>
            </div>
          </div>
          
          {/* Privacy Settings */}
          <div className="bg-white rounded-lg shadow-soft p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <FaUser className="mr-2 text-primary" /> Privacy Settings
            </h2>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label htmlFor="shareChildProgress" className="text-gray-700">
                  Share Child Progress with Schools
                </label>
                <div className="relative inline-block w-10 mr-2 align-middle select-none">
                  <input
                    type="checkbox"
                    id="shareChildProgress"
                    name="shareChildProgress"
                    checked={settings.privacy.shareChildProgress}
                    onChange={handlePrivacyChange}
                    className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                  />
                  <label
                    htmlFor="shareChildProgress"
                    className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"
                  ></label>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <label htmlFor="allowMentorContact" className="text-gray-700">
                  Allow Mentors to Contact You
                </label>
                <div className="relative inline-block w-10 mr-2 align-middle select-none">
                  <input
                    type="checkbox"
                    id="allowMentorContact"
                    name="allowMentorContact"
                    checked={settings.privacy.allowMentorContact}
                    onChange={handlePrivacyChange}
                    className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                  />
                  <label
                    htmlFor="allowMentorContact"
                    className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"
                  ></label>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <label htmlFor="showProfileToSchools" className="text-gray-700">
                  Show Profile to Schools
                </label>
                <div className="relative inline-block w-10 mr-2 align-middle select-none">
                  <input
                    type="checkbox"
                    id="showProfileToSchools"
                    name="showProfileToSchools"
                    checked={settings.privacy.showProfileToSchools}
                    onChange={handlePrivacyChange}
                    className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                  />
                  <label
                    htmlFor="showProfileToSchools"
                    className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"
                  ></label>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <label htmlFor="allowRecommendations" className="text-gray-700">
                  Enable Personalized Recommendations
                </label>
                <div className="relative inline-block w-10 mr-2 align-middle select-none">
                  <input
                    type="checkbox"
                    id="allowRecommendations"
                    name="allowRecommendations"
                    checked={settings.privacy.allowRecommendations}
                    onChange={handlePrivacyChange}
                    className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                  />
                  <label
                    htmlFor="allowRecommendations"
                    className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"
                  ></label>
                </div>
              </div>
            </div>
          </div>
          
          {/* Child Access Controls */}
          <div className="bg-white rounded-lg shadow-soft p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <FaUser className="mr-2 text-primary" /> Child Access Controls
            </h2>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label htmlFor="allowChildLogin" className="text-gray-700">
                  Allow Child Account Login
                </label>
                <div className="relative inline-block w-10 mr-2 align-middle select-none">
                  <input
                    type="checkbox"
                    id="allowChildLogin"
                    name="allowChildLogin"
                    checked={settings.childAccess.allowChildLogin}
                    onChange={handleChildAccessChange}
                    className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                  />
                  <label
                    htmlFor="allowChildLogin"
                    className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"
                  ></label>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <label htmlFor="restrictedAccess" className="text-gray-700">
                  Enable Restricted Access
                </label>
                <div className="relative inline-block w-10 mr-2 align-middle select-none">
                  <input
                    type="checkbox"
                    id="restrictedAccess"
                    name="restrictedAccess"
                    checked={settings.childAccess.restrictedAccess}
                    onChange={handleChildAccessChange}
                    className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                  />
                  <label
                    htmlFor="restrictedAccess"
                    className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"
                  ></label>
                </div>
              </div>
              
              <div>
                <label htmlFor="contentRestrictions" className="block text-sm font-medium text-gray-700 mb-1">
                  Content Restriction Level
                </label>
                <select
                  id="contentRestrictions"
                  name="contentRestrictions"
                  value={settings.childAccess.contentRestrictions}
                  onChange={handleChildAccessChange}
                  className="form-select w-full"
                >
                  <option value="none">None</option>
                  <option value="low">Low</option>
                  <option value="moderate">Moderate</option>
                  <option value="high">High</option>
                </select>
              </div>
              
              <div>
                <label htmlFor="screenTimeLimit" className="block text-sm font-medium text-gray-700 mb-1">
                  Daily Screen Time Limit (Minutes)
                </label>
                <input
                  type="number"
                  id="screenTimeLimit"
                  name="screenTimeLimit"
                  value={settings.childAccess.screenTimeLimit}
                  onChange={handleChildAccessChange}
                  min="0"
                  max="720"
                  step="15"
                  className="form-input w-full"
                />
              </div>
            </div>
          </div>
          
          {/* Preferences */}
          <div className="bg-white rounded-lg shadow-soft p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <FaGlobe className="mr-2 text-primary" /> Preferences
            </h2>
            
            <div className="space-y-4">
              <div>
                <label htmlFor="language" className="block text-sm font-medium text-gray-700 mb-1">
                  Language
                </label>
                <select
                  id="language"
                  name="language"
                  value={settings.preferences.language}
                  onChange={handlePreferenceChange}
                  className="form-select w-full"
                >
                  <option value="en">English</option>
                  <option value="sw">Swahili</option>
                  <option value="fr">French</option>
                </select>
              </div>
              
              <div>
                <label htmlFor="timezone" className="block text-sm font-medium text-gray-700 mb-1">
                  Timezone
                </label>
                <select
                  id="timezone"
                  name="timezone"
                  value={settings.preferences.timezone}
                  onChange={handlePreferenceChange}
                  className="form-select w-full"
                >
                  <option value="Africa/Nairobi">East Africa Time (EAT)</option>
                  <option value="Africa/Lagos">West Africa Time (WAT)</option>
                  <option value="Africa/Cairo">Eastern European Time (EET)</option>
                  <option value="Africa/Johannesburg">South Africa Standard Time (SAST)</option>
                </select>
              </div>
              
              <div>
                <label htmlFor="dateFormat" className="block text-sm font-medium text-gray-700 mb-1">
                  Date Format
                </label>
                <select
                  id="dateFormat"
                  name="dateFormat"
                  value={settings.preferences.dateFormat}
                  onChange={handlePreferenceChange}
                  className="form-select w-full"
                >
                  <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                  <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                  <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                </select>
              </div>
              
              <div>
                <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-1">
                  Currency
                </label>
                <select
                  id="currency"
                  name="currency"
                  value={settings.preferences.currency}
                  onChange={handlePreferenceChange}
                  className="form-select w-full"
                >
                  <option value="KES">Kenyan Shilling (KES)</option>
                  <option value="USD">US Dollar (USD)</option>
                  <option value="EUR">Euro (EUR)</option>
                  <option value="GBP">British Pound (GBP)</option>
                </select>
              </div>
              
              <div>
                <label htmlFor="theme" className="block text-sm font-medium text-gray-700 mb-1">
                  Theme
                </label>
                <select
                  id="theme"
                  name="theme"
                  value={settings.preferences.theme}
                  onChange={handlePreferenceChange}
                  className="form-select w-full"
                >
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                  <option value="system">System Default</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Save Button */}
      <div className="mt-6">
        <button
          type="button"
          onClick={handleSaveSettings}
          disabled={saving || loading}
          className="flex items-center px-6 py-3 bg-primary border border-transparent rounded-md shadow-sm text-base font-medium text-white hover:bg-primary-dark"
        >
          {saving ? <LoadingSpinner size="sm" className="mr-2" /> : <FaSave className="mr-2" />}
          Save Settings
        </button>
      </div>
    </div>
  );
};

export default ParentSettings; 